/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import * as React from 'react';
import { AuthSdkError } from '@okta/okta-auth-js';
import { compare } from 'compare-versions';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
var OktaContext = /*#__PURE__*/React.createContext(null);
var useOktaAuth = function useOktaAuth() {
  return React.useContext(OktaContext);
};
var OktaError = function OktaError(_ref) {
  var error = _ref.error;
  if (error.name && error.message) {
    return /*#__PURE__*/React.createElement("p", null, error.name, ": ", error.message);
  }
  return /*#__PURE__*/React.createElement("p", null, "Error: ", error.toString());
};
var Security = function Security(_ref) {
  var oktaAuth = _ref.oktaAuth,
    restoreOriginalUri = _ref.restoreOriginalUri,
    onAuthRequired = _ref.onAuthRequired,
    children = _ref.children;
  var _React$useState = React.useState(function () {
      if (!oktaAuth) {
        return null;
      }
      return oktaAuth.authStateManager.getAuthState();
    }),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    authState = _React$useState2[0],
    setAuthState = _React$useState2[1];
  React.useEffect(function () {
    if (!oktaAuth || !restoreOriginalUri) {
      return;
    }
    if (oktaAuth.options.restoreOriginalUri) {
      console.warn('Two custom restoreOriginalUri callbacks are detected. The one from the OktaAuth configuration will be overridden by the provided restoreOriginalUri prop from the Security component.');
    }
    oktaAuth.options.restoreOriginalUri = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(oktaAuth, originalUri) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                return _context.abrupt("return", restoreOriginalUri(oktaAuth, originalUri));
              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));
      return function (_x, _x2) {
        return _ref2.apply(this, arguments);
      };
    }();
  }, []);
  React.useEffect(function () {
    if (!oktaAuth) {
      return;
    }
    if (oktaAuth._oktaUserAgent) {
      oktaAuth._oktaUserAgent.addEnvironment("@okta/okta-react".concat("/", "6.10.0"));
    } else {
      console.warn('_oktaUserAgent is not available on auth SDK instance. Please use okta-auth-js@^5.3.1 .');
    }
    var currentAuthState = oktaAuth.authStateManager.getAuthState();
    if (currentAuthState !== authState) {
      setAuthState(currentAuthState);
    }
    var handler = function handler(authState) {
      setAuthState(authState);
    };
    oktaAuth.authStateManager.subscribe(handler);
    oktaAuth.start();
    return function () {
      oktaAuth.authStateManager.unsubscribe(handler);
    };
  }, [oktaAuth]);
  var oktaContextValue = React.useMemo(function () {
    return {
      oktaAuth: oktaAuth,
      authState: authState,
      _onAuthRequired: onAuthRequired
    };
  }, [oktaAuth, authState, onAuthRequired]);
  if (!oktaAuth) {
    var err = new AuthSdkError('No oktaAuth instance passed to Security Component.');
    return /*#__PURE__*/React.createElement(OktaError, {
      error: err
    });
  }
  if (!restoreOriginalUri) {
    var _err = new AuthSdkError('No restoreOriginalUri callback passed to Security Component.');
    return /*#__PURE__*/React.createElement(OktaError, {
      error: _err
    });
  }
  if (!oktaAuth._oktaUserAgent) {
    console.warn('_oktaUserAgent is not available on auth SDK instance. Please use okta-auth-js@^5.3.1 .');
  } else {
    var isAuthJsSupported = compare(oktaAuth._oktaUserAgent.getVersion(), {
      "minSupportedVersion": "5.3.1"
    }.minSupportedVersion, '>=');
    if (!isAuthJsSupported) {
      var _err2 = new AuthSdkError("\n        Passed in oktaAuth is not compatible with the SDK,\n        minimum supported okta-auth-js version is ".concat({
        "minSupportedVersion": "5.3.1"
      }.minSupportedVersion, ".\n      "));
      return /*#__PURE__*/React.createElement(OktaError, {
        error: _err2
      });
    }
  }
  return /*#__PURE__*/React.createElement(OktaContext.Provider, {
    value: oktaContextValue
  }, children);
};
function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
var withOktaAuth = function withOktaAuth(ComponentToWrap) {
  var WrappedComponent = function WrappedComponent(props) {
    var oktaAuthProps = useOktaAuth();
    return /*#__PURE__*/React.createElement(ComponentToWrap, _objectSpread$1(_objectSpread$1({}, oktaAuthProps), props));
  };
  WrappedComponent.displayName = 'withOktaAuth_' + (ComponentToWrap.displayName || ComponentToWrap.name);
  return WrappedComponent;
};
var handledRedirect = false;
var LoginCallback = function LoginCallback(_ref) {
  var errorComponent = _ref.errorComponent,
    _ref$loadingElement = _ref.loadingElement,
    loadingElement = _ref$loadingElement === void 0 ? null : _ref$loadingElement,
    onAuthResume = _ref.onAuthResume;
  var _useOktaAuth = useOktaAuth(),
    oktaAuth = _useOktaAuth.oktaAuth,
    authState = _useOktaAuth.authState;
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    callbackError = _React$useState2[0],
    setCallbackError = _React$useState2[1];
  var ErrorReporter = errorComponent || OktaError;
  React.useEffect(function () {
    var isInteractionRequired = oktaAuth.idx.isInteractionRequired || oktaAuth.isInteractionRequired.bind(oktaAuth);
    if (onAuthResume && isInteractionRequired()) {
      onAuthResume();
      return;
    }
    if (!handledRedirect) {
      oktaAuth.handleLoginRedirect()["catch"](function (e) {
        setCallbackError(e);
      });
      handledRedirect = true;
    }
  }, [oktaAuth]);
  var authError = authState === null || authState === void 0 ? void 0 : authState.error;
  var displayError = callbackError || authError;
  if (displayError) {
    return /*#__PURE__*/React.createElement(ErrorReporter, {
      error: displayError
    });
  }
  return loadingElement;
};
export { LoginCallback, OktaContext, Security, useOktaAuth, withOktaAuth };